/**
 * EC2 / Stratible API for Postgres-backed tables (replaces browser supabase.from(...)).
 * Payments: use getTradingApiBase() + /payments/cashfree/* (server-side Cashfree; no Supabase functions.invoke).
 */

export function getTradingApiBase(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:8000';
    if (h.includes('stratible.in')) return 'https://api.stratible.in';
  }
  return 'https://api.stratible.in';
}

async function parseJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function tradingDbFetch(
  path: string,
  init?: RequestInit & { adminHeaders?: Record<string, string> }
): Promise<{ ok: boolean; status: number; json: Record<string, unknown> }> {
  const { adminHeaders, ...rest } = init || {};
  const url = `${getTradingApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(adminHeaders || {}),
      ...(rest.headers || {}),
    },
  });
  const json = await parseJson(res);
  return { ok: res.ok, status: res.status, json };
}

export async function userProfileGet(userId: string, email?: string) {
  const q = new URLSearchParams();
  if (userId) q.set('user_id', userId);
  if (email) q.set('email', email);
  const { ok: httpOk, json } = await tradingDbFetch(`/user/profile?${q.toString()}`);
  const ok = httpOk && json.status === 'success';
  return {
    ok,
    data: json.user as Record<string, unknown> | undefined,
    error: json.status === 'error' ? String(json.message || 'request failed') : null,
    raw: json,
  };
}

export async function userProfilePatch(body: Record<string, unknown>) {
  const { ok: httpOk, json } = await tradingDbFetch('/user/profile', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return {
    ok: httpOk && json.status === 'success',
    data: json.user as Record<string, unknown> | undefined,
    error: json.status === 'error' ? String(json.message || 'patch failed') : null,
    raw: json,
  };
}

export async function userExistsByEmail(email: string) {
  const { ok: httpOk, json } = await tradingDbFetch(
    `/user/exists-email?email=${encodeURIComponent(email)}`
  );
  const exists = Boolean(json.exists);
  return {
    ok: httpOk && json.status === 'success',
    exists,
    user: json.user as { id?: string; email?: string } | undefined,
    error: json.status === 'error' ? String(json.message || 'lookup failed') : null,
  };
}

export async function userRegisterProfile(body: Record<string, unknown>) {
  const { ok: httpOk, json } = await tradingDbFetch('/user/register-profile', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return {
    ok: httpOk && json.status === 'success',
    data: json.user as Record<string, unknown> | undefined,
    error: json.status === 'error' ? String(json.message || 'register failed') : null,
    raw: json,
  };
}

export async function healthDbPing() {
  const { ok: httpOk, json } = await tradingDbFetch('/health/db');
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'db unhealthy') : null,
  };
}

export async function levelFeesList() {
  const { ok: httpOk, json } = await tradingDbFetch('/level-fees');
  const fees = (json.fees as unknown[]) || [];
  return {
    ok: httpOk && json.status === 'success',
    data: fees,
    error: json.status === 'error' ? String(json.message || 'failed') : null,
  };
}

export async function adminLevelFeesPut(
  fees: { level: number; fee: number; updated_at?: string }[],
  adminHeaders?: Record<string, string>
) {
  const { ok: httpOk, json } = await tradingDbFetch('/admin/level-fees', {
    method: 'PUT',
    body: JSON.stringify({ fees }),
    adminHeaders,
  });
  return {
    ok: httpOk && json.status === 'success',
    data: (json.fees as unknown[]) || [],
    error: json.status === 'error' ? String(json.message || 'save failed') : null,
  };
}

export async function orderInsert(row: Record<string, unknown>) {
  const { ok: httpOk, json } = await tradingDbFetch('/orders', {
    method: 'POST',
    body: JSON.stringify(row),
  });
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'insert failed') : null,
  };
}

export async function orderUpdateByOrderId(orderId: string, patch: Record<string, unknown>) {
  const { ok: httpOk, json } = await tradingDbFetch(`/orders/by-order-id/${encodeURIComponent(orderId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'update failed') : null,
  };
}

export async function closeRequestEnqueue(body: {
  user_id: string;
  symbol: string;
  close_percent: number;
}) {
  const { ok: httpOk, json } = await tradingDbFetch('/user/close-request', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'enqueue failed') : null,
  };
}

export async function killSwitchStatus() {
  const { ok: httpOk, json } = await tradingDbFetch('/kill-switch/status');
  return {
    ok: httpOk && json.status === 'success',
    killSwitchActive: Boolean(json.kill_switch_active),
    config: json.config as Record<string, unknown> | null | undefined,
    error: json.status === 'error' ? String(json.message || 'failed') : null,
  };
}

export async function killSwitchActivate(
  body: Record<string, unknown>,
  adminHeaders?: Record<string, string>
) {
  const { ok: httpOk, json } = await tradingDbFetch('/kill-switch/activate', {
    method: 'POST',
    body: JSON.stringify(body),
    adminHeaders,
  });
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'activate failed') : null,
  };
}

export async function killSwitchDeactivate(adminHeaders?: Record<string, string>) {
  const { ok: httpOk, json } = await tradingDbFetch('/kill-switch/deactivate', {
    method: 'POST',
    body: JSON.stringify({}),
    adminHeaders,
  });
  return {
    ok: httpOk && json.status === 'success',
    error: json.status === 'error' ? String(json.message || 'deactivate failed') : null,
  };
}
